import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges, Inject, PLATFORM_ID } from '@angular/core';
import { Phase, ProjectV2 } from '@app/interfaces';
import { SwiperModule } from 'swiper/angular';
import { Gallery, GalleryItem, ImageItem, GalleryModule, ImageSize, ThumbnailsPosition } from 'ng-gallery';
import { Lightbox } from 'ng-gallery/lightbox';
import { DevelopmentParticipations } from '@interfaces/app';
import { ImageOptimizerService, UserService } from '@app/services';
import { ToastrService } from 'ngx-toastr';
import { CountUpModule } from 'ngx-countup';
import { PipesModule } from "../../../../../pipes/pipe.module";

@Component({
  selector: 'mnf-pi-v2-images',
  standalone: true,
  imports: [
    CommonModule,
    SwiperModule,
    GalleryModule,
    CountUpModule,
    PipesModule
],
  templateUrl: './pi-v2-images.component.html',
  styleUrl: './pi-v2-images.component.scss'
})
export class PiV2ImagesComponent implements OnInit, OnChanges {
  @Input() devParts: DevelopmentParticipations;
  @Input() infoProject: ProjectV2;
  @Input() phase: Phase;
  @Input() totalPartsUser: number;
  @Input() imagesCarrusel: string[];
  showShareToast: boolean = false;

  @ViewChild('itemTemplate') itemTemplate;

  items: GalleryItem[] = [];
  public readonly urlBaseS3 = 'https://static.monific.com/assets/projects-images/';
  private readonly JWT_TOKEN = 'JWT_TOKEN';
  private readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
  private readonly ID_TOKEN = 'ID_TOKEN';
  private readonly EXP_AUTH = 'EXP_AUTH';
  
  percentage: number | string;
  message: string | number;
  labelFreeParts: string = 'Proyecto en proceso de fondeo';
  restDayTotal: Number;
  imageLoaded: boolean = true;
  isBrowser: boolean;
  browserContainer: boolean;

  isReferralInternal: boolean = false;
  codeReferralInternal: string = '';

  
  shareToastItems = [
    {
      class: 'whatsapp',
      icon: '/assets/icons/whatsapp.svg',
      alt: 'whatsapp',
      name: 'Whatsapp',
      url: '',
    },
    {
      class: 'copy-link',
      icon: '/assets/icons/paperclip.svg',
      alt: 'paperclip',
      onClick: () => this.copyLink(),
      name: 'Copiar enlace',
    }
  ];
  
  stepProject: number | string = 1;
  constructor(private gallery: Gallery, public lightbox: Lightbox, @Inject(PLATFORM_ID) private platformId, private userServ: UserService, private toastServ: ToastrService, private imageOptimizerServ: ImageOptimizerService) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if(this.isBrowser){
      this.checkScreenSize();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.infoProject && changes.infoProject.currentValue) {
      this.updateStepProject();
    }

    if (changes.phase && changes.phase.currentValue) {
      this.calculateFundingStatus();
    }

    if (changes.imagesCarrusel && changes.imagesCarrusel.currentValue) {
      this.loadGalleryImages();
    }
  }

  updateLinksInternalReferral() {
    if(sessionStorage.getItem(this.JWT_TOKEN) && sessionStorage.getItem(this.REFRESH_TOKEN) && sessionStorage.getItem(this.ID_TOKEN) && sessionStorage.getItem(this.EXP_AUTH)) {
      this.userServ.initInternalReferral().subscribe(res => {
        if(res?.id_referral) {
          this.isReferralInternal = true;
          this.codeReferralInternal = `https://app.monific.com/proyecto/v2/${this.infoProject?.pid}?intrff=${res?.id_referral}`;
          this.shareToastItems[0].url = `https://wa.me/?text=${encodeURIComponent(this.codeReferralInternal)}`;
        }
      });
    }
  }  


  copyLink(): void {
    if (this.codeReferralInternal) {
      navigator.clipboard.writeText(this.codeReferralInternal).then(() => {
        this.toastServ.success('Enlace copiado al portapapeles');
      }).catch(err => {
        console.error('Error al copiar enlace:', err);
      });
    }
  }
  


  private updateStepProject(): void {
    const pid = this.infoProject?.pid;
    if (pid) {
      let isWithoutStep = pid.split("").splice(-2).join("");

      this.updateLinksInternalReferral();

      if(isWithoutStep.match(/[a-z]/i)) {
        this.stepProject = "1";
      } else if(isWithoutStep.includes("-")) {
        this.stepProject = isWithoutStep.split("-").pop();
      } else {
        this.stepProject = "1";
      }
    }
  }

  private calculateFundingStatus(): void {
    const total = Number(this.phase?.total);
    let minAmountProject = Number(this.phase?.minAmountProject);
    if (minAmountProject == 0) {
      minAmountProject = this.calculateAutoPercent(total);
    }
    const active = Number(this.phase?.active);

    let percentMinAmount = (Number(active) / Number(minAmountProject)) * 90;
    if (this?.phase?.free == 0) {
      this.percentage = 100;
      this.message = `100%`;
      this.labelFreeParts = '<div class="text-center">Proyecto fondeado</div>';
    } else if (total == minAmountProject) {
      this.percentage = (Number(active) / Number(total)) * 100;
      this.message = `${this.percentage.toFixed(2)}%`;
      this.labelFreeParts = `<div class="fw-sbold text-start">Restan <strong class='part'>${total - active} participaciones </strong> para invertir</div>`;
    } else if (percentMinAmount >= 90) {
      this.percentage = 90;
      this.message = 'Mínimo alcanzado';
      this.labelFreeParts = `<div class="fw-sbold text-start">Restan <strong class='part'>${total - active} participaciones </strong> para invertir</div>`;
      if (active >= minAmountProject) {
        let percentTotal = (Number(active) / Number(total)) * 100;
        if (percentTotal >= 95) {
          this.percentage = percentTotal;
        }
      }
    } else if (percentMinAmount <= 90) {
      this.percentage = (Number(active) / Number(minAmountProject)) * 100;
      this.message = this.percentage.toFixed(2) + '%';
      this.labelFreeParts = `Restan <strong class='part'>${minAmountProject - active} participaciones </strong> para llegar al objetivo mínimo`;
    }
  }

  private loadGalleryImages(): void {
    this.items = this.imagesCarrusel?.map((img) => {
      let src = this.buildGalleryImageUrl(img);
      return new ImageItem({ src: src, thumb: src });
    });

    const lightboxRef = this.gallery.ref('lightbox');

    lightboxRef.setConfig({
      imageSize: ImageSize.Cover,
      thumbPosition: ThumbnailsPosition.Top,
    });

    lightboxRef.load(this.items);
  }

  calcuateRestDay(dateLimit: string): number {
    const dateNow = new Date();
    const dateEnd = new Date(Number(dateLimit));
    const diffTime = dateEnd?.getTime() - dateNow.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (Number.isNaN(diffDays) || diffDays < 0 || diffDays == null || diffDays == undefined) {
      return 60;
    } else {
      return diffDays;
    }
  }

  getProjectStateText(state: string): string {
    const stateTextMap = {
      'fixed': 'Rendimiento fijo',
      'variable': 'Rendimiento variable',
    };
  
    return stateTextMap[state] || 'Rendimiento fijo';
  }

  formatName(name: string | undefined): string {
    if (!name) return "";
    const regex = /\s-\s\d+$/;
    if (regex.test(name)) {
      return name.replace(regex, "");
    }
    return name;
  }

  buildGalleryImageUrl(imagePath: string): string {
    let basePath = this.infoProject?.baseProject || this.infoProject?.pid;
    let fullUrl = this.urlBaseS3 + basePath + '/' + imagePath;
  
    let urlParts = fullUrl.split("https://static.monific.com/assets/");
    if (urlParts.length > 2) {
      fullUrl = "https://static.monific.com/assets/" + urlParts.pop();
    }
  
    return this.imageOptimizerServ.getOptimizedImageUrl(fullUrl, 1440);
  }

  rightPosition(): number {
    const percentageAsNumber = typeof this.percentage === 'string' ? parseFloat(this.percentage) : this.percentage;

    if (percentageAsNumber < 10) {
      return -55;
    } else if (percentageAsNumber > 90) {
      return -7;
    } else {
      return -28;
    }
  }

  getSpanTranslate(): string {
    const percentageAsNumber = typeof this.percentage === 'string' ? parseFloat(this.percentage) : this.percentage;
    if (percentageAsNumber < 10) {
      return 'translate(-1.6rem, 1.7em) rotate(45deg)';
    } else if (percentageAsNumber > 90) {
      return 'translate(1.4rem, 1.9em) rotate(45deg)';
    } else {
      return 'translate(0.2em, 1.9em) rotate(45deg)';
    }
  }

  calculateAutoPercent(number: number) {
    return number * 0.20;
  }

  checkScreenSize(){
    if(!this.isBrowser) return;
    const isLargeScreen = window.innerWidth >= 992;
    if(isLargeScreen){
      this.browserContainer = true;
    }else{
      this.browserContainer = false;
    }
  }

  changeStateToastShare(){
    this.showShareToast = !this.showShareToast;
  }
}
